// The header: the mark, the sections, the theme switch, the app's sign-in and the call to join.
// Below `lg` the sections fold into a sheet.
import { List, Moon, Sun, X } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router";

import { Logo } from "./Logo";
import { APP_URL, NAV } from "./nav";
import { useTheme } from "./theme";

function ThemeSwitch() {
  const [theme, toggle] = useTheme();
  return (
    <button type="button" onClick={toggle} className="rounded-full p-2 text-ink2 hover:bg-paper2 hover:text-ink" aria-label={theme === "dark" ? "Use the light theme" : "Use the dark theme"}>
      {theme === "dark" ? <Sun weight="bold" className="h-[18px] w-[18px]" /> : <Moon weight="bold" className="h-[18px] w-[18px]" />}
    </button>
  );
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  clsx("relative rounded-full px-3 py-1.5 text-[14.5px] font-medium transition-colors", isActive ? "text-ink" : "text-ink2 hover:text-ink", isActive && "after:absolute after:inset-x-3 after:-bottom-0.5 after:h-[2px] after:rounded after:bg-signal");

export function Header() {
  // The sheet belongs to the page it was opened on: following a link closes it.
  const { pathname } = useLocation();
  const [openOn, setOpenOn] = useState<string>();
  const open = openOn === pathname;

  return (
    <header className="sticky top-0 z-40 border-b border-line/70 bg-paper/85 backdrop-blur-md">
      <div className="wrap flex h-16 items-center justify-between gap-4">
        <Link to="/" aria-label="OpenCloudSecurity, home" className="shrink-0">
          <Logo />
        </Link>
        <nav aria-label="Sections" className="hidden items-center gap-0.5 lg:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkClass}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1.5">
          <ThemeSwitch />
          <a href={APP_URL} className="hidden rounded-full px-3 py-1.5 text-[14px] font-medium text-ink2 hover:text-ink sm:inline-flex">
            Sign in
          </a>
          <Link to="/charter#sign" className="btn-ink hidden !py-2 sm:inline-flex">
            Join the movement
          </Link>
          <button type="button" className="rounded-full p-2 text-ink lg:hidden" onClick={() => setOpenOn(open ? undefined : pathname)} aria-expanded={open} aria-label="Menu">
            {open ? <X weight="bold" className="h-5 w-5" /> : <List weight="bold" className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open ? (
        <nav aria-label="Sections" className="border-t border-line bg-paper lg:hidden">
          <div className="wrap flex flex-col py-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className="border-b border-line/60 py-3 font-display text-2xl font-bold tracking-tight last:border-0">
                {n.label}
              </NavLink>
            ))}
            <div className="mt-4 flex gap-2 pb-2">
              <Link to="/charter#sign" className="btn-signal flex-1">
                Join the movement
              </Link>
              <a href={APP_URL} className="btn-line">
                Sign in
              </a>
            </div>
          </div>
        </nav>
      ) : null}
    </header>
  );
}
